import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { FileText, Search, Filter, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../../api';

const POC485Page = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [includeInactivated, setIncludeInactivated] = useState(false);

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const response = await api.get('/patients');
        if (response.data.success) {
          setPatients(response.data.patients);
        }
      } catch (error) {
        console.error('Error fetching patients:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPatients();
  }, []);

  const filteredPatients = patients.filter(patient =>
    `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredPatients.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const paginatedPatients = filteredPatients.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header - Alora Plus Style */}
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-[#3498db]">
            485 Certification & POC
            <span className="text-gray-500 font-normal text-lg ml-2">&gt; Patient Admission Search</span>
          </h1>
          <Button 
            onClick={() => navigate('/dashboard/clinical/poc-485/form')}
            className="bg-[#27ae60] hover:bg-[#219a52]"
          >
            + New POC/485
          </Button>
        </div>

        {/* Search Bar - Alora Plus Style */}
        <div className="bg-white rounded shadow-sm border p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Search:</span>
              <div className="relative">
                <Input
                  placeholder=""
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-48 h-8 text-sm"
                />
              </div>
              <Button size="sm" className="bg-[#3498db] hover:bg-[#2980b9] h-8 px-3">
                <Filter className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">Show</span>
              <select 
                value={entriesPerPage}
                onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                className="border rounded px-2 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="text-gray-600">entries</span>
            </div>
          </div>
        </div>

        {/* Patient Table - Alora Plus Style */}
        <div className="bg-white rounded shadow-sm border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-[#3498db]">Select</th>
                <th className="px-4 py-3 text-left font-semibold text-[#3498db]">Patient Name</th>
                <th className="px-4 py-3 text-left font-semibold text-[#3498db]">PAN #</th>
                <th className="px-4 py-3 text-left font-semibold text-[#3498db]">Admit Date</th>
                <th className="px-4 py-3 text-left font-semibold text-[#3498db]">Discharge Date</th>
                <th className="px-4 py-3 text-left font-semibold text-[#3498db]">DOB</th>
                <th className="px-4 py-3 text-left font-semibold text-[#3498db]">Office Name</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : paginatedPatients.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">No patients found</td>
                </tr>
              ) : (
                paginatedPatients.map((patient, index) => (
                  <tr key={patient.id} className="hover:bg-blue-50 cursor-pointer" onClick={() => navigate(`/dashboard/clinical/poc-485/edit?patientId=${patient.id}`)}>
                    <td className="px-4 py-3">
                      <ExternalLink className="w-4 h-4 text-[#3498db] cursor-pointer" />
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {patient.lastName?.toUpperCase()}, {patient.firstName?.toUpperCase()}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{startIndex + index + 1}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {patient.admissionDate ? (([y, m, d]) => `${m}/${d}/${y}`)(String(patient.admissionDate).slice(0, 10).split('-')) : ''}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {patient.dischargeDate ? new Date(patient.dischargeDate).toLocaleDateString() : ''}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {/^\d{4}-\d{2}-\d{2}/.test(patient.dateOfBirth || '') ? (([y, m, d]) => `${m}/${d}/${y}`)(patient.dateOfBirth.slice(0, 10).split('-')) : patient.dateOfBirth || ''}
                    </td>
                    <td className="px-4 py-3 text-gray-600">Ivory Home Health Care, Inc.</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination - Alora Plus Style */}
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <div className="text-sm text-gray-600">
              Showing {startIndex + 1} to {Math.min(endIndex, filteredPatients.length)} of {filteredPatients.length} entries
            </div>
            <div className="flex items-center gap-1">
              <button 
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded text-sm hover:bg-gray-100 disabled:opacity-50"
              >
                First
              </button>
              <button 
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded text-sm hover:bg-gray-100 disabled:opacity-50"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => goToPage(pageNum)}
                    className={`px-3 py-1 border rounded text-sm ${currentPage === pageNum ? 'bg-[#3498db] text-white' : 'hover:bg-gray-100'}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button 
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-3 py-1 border rounded text-sm hover:bg-gray-100 disabled:opacity-50"
              >
                Next
              </button>
              <button 
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-3 py-1 border rounded text-sm hover:bg-gray-100 disabled:opacity-50"
              >
                Last
              </button>
            </div>
          </div>
        </div>

        {/* Include Inactivated Checkbox */}
        <div className="mt-4">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={includeInactivated}
              onChange={(e) => setIncludeInactivated(e.target.checked)}
              className="rounded border-gray-300"
            />
            Include Inactivated Admissions
          </label>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default POC485Page;
